import { BybButton } from "@Components/common/byb-ds";
import { DialogSelectButton } from "@Components/game/common";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { ChallengeStatus } from "@Enums/index";
import { faSlidersSimple, faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Button, Dialog, IconButton, Slide, Stack, Typography, useTheme } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { forwardRef, useEffect, useState } from "react";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const filters = [
  { label: "challenges.filter.not_started", value: ChallengeStatus.NOT_STARTED },
  { label: "challenges.filter.completed", value: ChallengeStatus.COMPLETED },
  { label: "challenges.filter.failed", value: ChallengeStatus.FAILED },
  { label: "challenges.filter.locked", value: "locked" },
];

interface IChallengeFilterDialog {
  open: boolean;
  onApply: (filters: string[]) => void;
  onClose: () => void;
}

const ChallengeFilterDialog = ({ open, onApply, onClose }: IChallengeFilterDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const [open_, setOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const toggleOpen = (open: boolean) => setOpen(open);

  const handleClose = () => {
    toggleOpen(false);
    onClose();
  };

  const handleClear = () => {
    handleClose();
    setActiveFilters([]);
    onApply([]);
  };

  const handleApply = () => {
    handleClose();
    onApply(activeFilters);
  };

  const handleFilterClick = (filter: string) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  useEffect(() => {
    toggleOpen(open);
  }, [open]);

  return (
    <Dialog
      fullScreen
      open={open_}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        "& .MuiDialog-paper": {
          background: theme.palette.dsSurface.v200,
          boxShadow: "none",
        },
      }}
    >
      <Stack justifyContent="space-between" height="100%">
        <Stack spacing={6}>
          <Stack
            direction="row"
            sx={{
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
              boxSizing: "border-box",
              padding: theme.spacing(4, 6),
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Icon icon={faSlidersSimple} color={theme.palette.dsBase.v200} fontSize={24} />
              <Typography variant="textLg500" color="dsBase.v200">
                {i18n("challenges.filter.title")}
              </Typography>
            </Stack>

            <HapticsButtonWrapper>
              <IconButton
                onClick={handleClose}
                sx={{
                  backdropFilter: "blur(12px)",
                  background: theme.palette.dsSurface.v300,
                  padding: theme.spacing(1.5),
                }}
              >
                <Icon icon={faXmarkCircle} color={theme.palette.dsBase.v200} fontSize={24} />
              </IconButton>
            </HapticsButtonWrapper>
          </Stack>

          {/* four filter buttons */}
          <Stack direction="row" spacing={3} sx={{ padding: theme.spacing(0, 6) }}>
            {filters.map(filter => (
              <DialogSelectButton
                key={filter.value}
                variant={activeFilters.includes(filter.value) ? "contained" : "outlined"}
                onClick={() => handleFilterClick(filter.value)}
                sx={{ borderRadius: theme.spacing(5), padding: theme.spacing(3) }}
                fullWidth
              >
                {i18n(filter.label)}
              </DialogSelectButton>
            ))}
          </Stack>
        </Stack>
        <Stack sx={{ padding: theme.spacing(3, 6) }} alignItems="center" justifyContent="center">
          <Stack direction="row" spacing={3}>
            <Button color="secondary" size="large" onClick={handleClear}>
              {i18n("challenges.filter.clear")}
            </Button>
            <Button color="primary" size="large" onClick={handleApply}>
              {i18n("challenges.filter.apply", { count: activeFilters.length })}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default ChallengeFilterDialog;
